import {CommonModule} from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  inject,
  input,
  OnDestroy,
  OnInit,
  output,
  Renderer2,
  signal,
} from '@angular/core';
import {NavigationEnd, Router} from '@angular/router';
import {TranslocoService} from '@jsverse/transloco';
import {ORDERS} from '../../constants';
import {EMPTY, forkJoin, Observable, of, Subject} from 'rxjs';
import {first, map, switchMap, takeUntil, tap, withLatestFrom} from 'rxjs/operators';
import {AsmImpersonateUserInformationComponent} from '../../../features/asm/ui/asm-impersonate-user-information/asm-impersonate-user-information.component';
import {UserData} from '../../../interfaces/user-data.interface';
import {AsmService} from '../../services/asm.service';
import {UserService} from '../../services/user.service';
import {asmNavigation} from './asm-navigation';
import {MainNavigationItemComponent} from './main-navigation-item/main-navigation-item.component';
import {navigation} from './navigation';
import {NavigationItem} from './types/navigation.types';

@Component({
  selector: 'cov-sidebar-navigation',
  imports: [CommonModule, MainNavigationItemComponent, AsmImpersonateUserInformationComponent],
  templateUrl: './sidebar-navigation.component.html',
  styleUrl: './sidebar-navigation.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SidebarNavigationComponent implements OnInit, OnDestroy {
  // SERVICES
  private userService = inject(UserService);
  private asmService = inject(AsmService);
  private renderer = inject(Renderer2);
  private elementRef = inject(ElementRef);
  private router = inject(Router);
  private translocoService = inject(TranslocoService);

  // INPUT
  protected impersonateActiveSession$ = this.asmService.getImpersonateActiveSession();
  public readonly mainNavigation = navigation;
  public assistentServiceModeNavigation = asmNavigation;
  public filteredMainNavigation = signal<NavigationItem[]>([]);
  public expanded = input.required<boolean>();
  // if false, all nav groups collapse when another one opens
  public multi = input<boolean>(true);
  public navigation = output<void>();
  public asmNavigation = output<void>();

  // STATE
  public activeFloatingMenuNavItem = signal<NavigationItem | null>(null);
  // currently open nav groups
  public openNavGroup = signal<NavigationItem[]>([]);
  private documentClickHandler: (() => void) | null = null;
  public isAsmAgent = this.asmService.isAsmAgent;

  private destroy$ = new Subject<void>();

  public filterMainNavigation = this.userService.getCurrentUser().pipe(
    switchMap((user) => {
      if (!user) {
        return of([]);
      }
      return this.filterNavigationItemsRecursive(this.mainNavigation, user);
    }),
    tap((result) => {
      this.filteredMainNavigation.set(result);
      this.expandCurrentNaviItemGroup();
    }),
  );

  private translateItem(item: NavigationItem): NavigationItem {
    const translatedLabel = this.translocoService.translate(item.label);
    return {
      ...item,
      label: translatedLabel,
      children: item.children?.map((child) => this.translateItem(child)) || [],
    };
  }

  public ngOnInit() {
    this.assistentServiceModeNavigation = this.assistentServiceModeNavigation.map((item) => {
      return this.translateItem(item);
    });

    this.documentClickHandler = this.renderer.listen('document', 'click', (event) => {
      this.handleClickOutside(event);
    });

    // react to router events to update navigation state
    this.router.events.pipe(takeUntil(this.destroy$)).subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.clearFloatingMenu();
        this.navigation.emit();
        this.expandCurrentNaviItemGroup(); //triggers closing other menus
      }
    });

    this.filterMainNavigation.pipe(takeUntil(this.destroy$)).subscribe();

    this.translocoService.events$
      .pipe(
        withLatestFrom(this.userService.getCurrentUser()),
        switchMap(([event, user]) => {
          if (!user) {
            return EMPTY;
          }
          return this.filterNavigationItemsRecursive(this.mainNavigation, user);
        }),
        tap((result) => {
          this.filteredMainNavigation.set(result);
          this.expandCurrentNaviItemGroup();
        }),
        takeUntil(this.destroy$),
      )
      .subscribe();
  }

  public ngOnDestroy() {
    if (this.documentClickHandler) {
      this.documentClickHandler();
    }
    this.destroy$.next();
    this.destroy$.complete();
  }

  private filterNavigationItemsRecursive(
    navItems: NavigationItem[],
    user: UserData,
  ): Observable<NavigationItem[]> {
    const checks = navItems.map((navItem) => {
      const hasPermission$ =
        navItem.permissions.length > 0
          ? this.userService.hasAnyPermission$(navItem.permissions).pipe(first())
          : of(true);

      const isNegotiatorAllowed$ = navItem.requiresNegotiator
        ? this.userService.isNegotiator$().pipe(first())
        : of(true);

      const isAllowed$ = forkJoin([hasPermission$, isNegotiatorAllowed$]).pipe(
        map(([hasPermission, isNegotiator]) => hasPermission && isNegotiator),
      );

      return isAllowed$.pipe(
        switchMap((isAllowed) => {
          if (isAllowed) {
            const children$ =
              navItem.children.length > 0
                ? this.filterNavigationItemsRecursive(navItem.children, user)
                : of([]);

            return children$.pipe(
              map((filteredChildren) => {
                const translatedNavItem = this.translateItem({
                  ...navItem,
                  children: filteredChildren,
                });
                return translatedNavItem;
              }),
            );
          } else {
            return of(null);
          }
        }),
      );
    });

    return forkJoin(checks).pipe(
      map((results) => results.filter((navItem): navItem is NavigationItem => !!navItem)),
    );
  }

  public setActiveFloatingMenuNavItem(navItem: NavigationItem): void {
    if (this.activeFloatingMenuNavItem() === navItem) {
      this.clearFloatingMenu();
      return;
    }
    this.activeFloatingMenuNavItem.set(navItem);
  }

  public handleClickOutside(event: Event) {
    if (!this.elementRef?.nativeElement.contains(event.target)) {
      this.clearFloatingMenu();
    }
  }

  public setOpenNaviGroups(navItem: NavigationItem): void {
    if (this.openNavGroup()?.includes(navItem)) {
      return;
    }

    const newSet = this.multi() ? [...this.openNavGroup(), navItem] : [navItem];

    this.openNavGroup.set(newSet);
  }

  private clearFloatingMenu() {
    this.activeFloatingMenuNavItem.set(null);
  }

  private expandCurrentNaviItemGroup() {
    const activeGroupItem = this.filteredMainNavigation().find(
      (nav) => nav.children?.length && this.router.url.startsWith(nav.route),
    );

    if (activeGroupItem) {
      this.openNavGroup.set([activeGroupItem]);
    } else {
      const ordersItem = this.filteredMainNavigation().find((nav) => nav.route === ORDERS);

      this.openNavGroup.set(ordersItem ? [ordersItem] : []);
    }
  }
}
